/**
 * PR-F — 10 AutoCAD arrowhead mesh prototypes.
 *
 * Each prototype is a `THREE.BufferGeometry` at unit size (1 world
 * unit along the dim-line direction, ±0.5 wide). Per-dimension scale
 * + rotation is applied at render time via `InstancedMesh` per-cell
 * matrices, so a 10 000-arrow drawing pays for 10 geometries total
 * regardless of count.
 *
 * # Coordinate convention
 *
 * Every prototype lives in 2D (z = 0). The arrow's tip is at the
 * origin and the body extends along the **negative X axis** (toward
 * (-1, 0)). At render time:
 *
 *   1. Translate the prototype by `tip` (world coordinate).
 *   2. Rotate by `atan2(direction.y, direction.x)` so the tip points
 *      along the dim-line direction.
 *   3. Scale by `DIMASZ × DIMSCALE` (or `MLENT_ARROWHEAD_SIZE × scale`).
 *
 * The negative-X body convention matches AutoCAD's own arrow blocks:
 * the block insertion point is the tip, the body extends "back" into
 * the dimension line. A reverse arrow at the other end of the dim
 * line gets `direction = -dimDir` so its body points the other way.
 *
 * # Variants (10)
 *
 *   1. `closed-filled` — the AutoCAD default (filled triangle).
 *   2. `closed` — outlined triangle, fill = stroke.
 *   3. `closed-blank` — outlined triangle, no fill.
 *   4. `open` — V-shaped open angle (no closing line).
 *   5. `open30` — 30° open angle, narrower than `open`.
 *   6. `dot` — filled circle.
 *   7. `dot-blank` — outlined circle, no fill.
 *   8. `arch-tick` — 45° architectural slash.
 *   9. `oblique` — 90° (vertical) slash.
 *   10. `none` — empty geometry (no draw).
 *
 * Mapping `_DIMBLK` → variant lives in `dimblkToVariant()` below;
 * shared between dimension + multileader.
 *
 * # Rendering pipeline integration
 *
 *   * Filled variants (`closed-filled`, `closed`, `dot`, `arch-tick`,
 *     `oblique`) → triangle mesh (`buildFilledGeometry()`).
 *   * Outline variants (`closed-blank`, `dot-blank`, `open`,
 *     `open30`) → line-segment list (`buildOutlineGeometry()`).
 *   * `none` → empty BufferGeometry (renderer skips).
 *
 * The renderer dispatches on the variant's `fill` flag returned by
 * `getArrowPrototype()` to choose between MeshBasicMaterial and
 * LineSegments.
 *
 * # CLAUDE.md ПРИОРИТЕТ #1
 *
 * AutoCAD parity is exact: same 10 variants as the SVG renderer's
 * `ArrowMarkerDefs.tsx`, same DIMBLK name → variant mapping, same
 * relative proportions (1 unit length × 0.5 unit half-width =
 * 11.4° half-angle, AutoCAD's standard arrow geometry).
 */

import * as THREE from "three";

import type { ArrowVariant, Vec3 } from "../types";

/** What the renderer needs to draw a prototype. */
export interface ArrowPrototype {
  variant: ArrowVariant;
  /** True → triangle mesh; false → outlined (LineSegments). */
  fill: boolean;
  /** Pre-computed unit-size BufferGeometry. */
  geometry: THREE.BufferGeometry;
}

const HALF_WIDTH = 0.5;

/** Build a filled triangle: tip at (0,0), base at (-1, ±0.5). */
function buildClosedFilled(): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  const verts = new Float32Array([
    0, 0, 0,
    -1, HALF_WIDTH, 0,
    -1, -HALF_WIDTH, 0,
  ]);
  g.setAttribute("position", new THREE.BufferAttribute(verts, 3));
  g.setIndex([0, 1, 2]);
  return g;
}

/**
 * Outlined-triangle vertices for `closed-blank` and `closed` (when
 * rendered as outline). 4 line segments: tip→top, top→bottom, bottom→tip.
 * Returns a 6-vertex pair list (3 line segments × 2 endpoints).
 */
function buildClosedOutline(): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  const verts = new Float32Array([
    0, 0, 0, -1, HALF_WIDTH, 0,
    -1, HALF_WIDTH, 0, -1, -HALF_WIDTH, 0,
    -1, -HALF_WIDTH, 0, 0, 0, 0,
  ]);
  g.setAttribute("position", new THREE.BufferAttribute(verts, 3));
  return g;
}

/** Open V: tip→top corner + tip→bottom corner. No closing edge. */
function buildOpenV(halfAngleRad: number): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  // For half angle θ, the corner at (-1, ±tan(θ)) — but we cap the
  // length at 1 along x. AutoCAD's standard arrow uses ~26.5° half
  // angle (atan(0.5)); the narrower `open30` uses 15° half angle.
  const dy = Math.tan(halfAngleRad);
  const verts = new Float32Array([
    0, 0, 0, -1, dy, 0,
    0, 0, 0, -1, -dy, 0,
  ]);
  g.setAttribute("position", new THREE.BufferAttribute(verts, 3));
  return g;
}

/**
 * Filled circle approximated by a 16-segment triangle fan. Centre at
 * (-0.5, 0), radius 0.5, so the dot's right edge sits exactly at the
 * tip (0,0) and the left edge at (-1,0) — same total footprint as
 * the triangle arrows.
 */
function buildDotFilled(): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  const segments = 16;
  const cx = -0.5;
  const r = 0.5;
  const verts: number[] = [cx, 0, 0]; // centre
  for (let i = 0; i <= segments; i++) {
    const a = (i / segments) * Math.PI * 2;
    verts.push(cx + r * Math.cos(a), r * Math.sin(a), 0);
  }
  const idx: number[] = [];
  for (let i = 1; i <= segments; i++) {
    idx.push(0, i, i + 1);
  }
  g.setAttribute(
    "position",
    new THREE.BufferAttribute(new Float32Array(verts), 3),
  );
  g.setIndex(idx);
  return g;
}

/**
 * Outlined circle as a 16-segment LineSegments pair list (32 verts =
 * 16 line segments).
 */
function buildDotOutline(): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  const segments = 16;
  const cx = -0.5;
  const r = 0.5;
  const verts: number[] = [];
  for (let i = 0; i < segments; i++) {
    const a0 = (i / segments) * Math.PI * 2;
    const a1 = ((i + 1) / segments) * Math.PI * 2;
    verts.push(cx + r * Math.cos(a0), r * Math.sin(a0), 0);
    verts.push(cx + r * Math.cos(a1), r * Math.sin(a1), 0);
  }
  g.setAttribute(
    "position",
    new THREE.BufferAttribute(new Float32Array(verts), 3),
  );
  return g;
}

/**
 * Architectural tick (45° slash). Two triangles forming a 0.2-thick
 * stroke from (-0.5, -0.5) to (-0.5+1, +0.5) — i.e. a slash crossing
 * the tip. AutoCAD draws this as a single flat tick of fixed
 * thickness, scaled by DIMASZ.
 */
function buildArchTick(): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  // A 45° slash from lower-left to upper-right of the unit cell.
  // We render as a thin filled strip (two triangles) of half-width
  // 0.06 so the slash is visible at unit scale even when the
  // dimension's lineweight is 0.
  const hw = 0.06;
  // Slash vector (1, 1) normalised; perpendicular = (-1, 1)/√2 × hw.
  const px = -hw / Math.SQRT2;
  const py = hw / Math.SQRT2;
  // Slash endpoints: tip-side (0.45, 0.45) and back-side (-0.45, -0.45).
  const x0 = -0.45;
  const y0 = -0.45;
  const x1 = 0.45;
  const y1 = 0.45;
  const verts = new Float32Array([
    x0 + px, y0 + py, 0,
    x0 - px, y0 - py, 0,
    x1 + px, y1 + py, 0,
    x1 - px, y1 - py, 0,
  ]);
  g.setAttribute("position", new THREE.BufferAttribute(verts, 3));
  g.setIndex([0, 1, 2, 1, 3, 2]);
  return g;
}

/**
 * Oblique tick (vertical slash through the tip). Same as arch-tick but
 * the slash is vertical (perpendicular to the dim line direction).
 */
function buildObliqueTick(): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  const hw = 0.06;
  // Vertical slash from (-0, -0.5) to (0, +0.5) with half-width hw
  // along the dim line direction (X axis).
  const verts = new Float32Array([
    -hw, -0.5, 0,
    hw, -0.5, 0,
    -hw, 0.5, 0,
    hw, 0.5, 0,
  ]);
  g.setAttribute("position", new THREE.BufferAttribute(verts, 3));
  g.setIndex([0, 1, 2, 1, 3, 2]);
  return g;
}

/* ── Singleton prototype cache ───────────────────────────────────── */

let CACHE: Map<ArrowVariant, ArrowPrototype> | null = null;

function buildAllPrototypes(): Map<ArrowVariant, ArrowPrototype> {
  const m = new Map<ArrowVariant, ArrowPrototype>();
  m.set("closed-filled", {
    variant: "closed-filled",
    fill: true,
    geometry: buildClosedFilled(),
  });
  m.set("closed", {
    variant: "closed",
    fill: true,
    geometry: buildClosedFilled(),
  });
  m.set("closed-blank", {
    variant: "closed-blank",
    fill: false,
    geometry: buildClosedOutline(),
  });
  // ~26.5° half angle for default open variant; matches AutoCAD's
  // standard "OPEN" block (tan ≈ 0.5).
  m.set("open", {
    variant: "open",
    fill: false,
    geometry: buildOpenV(Math.atan(0.5)),
  });
  // ~15° half angle for the narrower OPEN30 block.
  m.set("open30", {
    variant: "open30",
    fill: false,
    geometry: buildOpenV((15 * Math.PI) / 180),
  });
  m.set("dot", {
    variant: "dot",
    fill: true,
    geometry: buildDotFilled(),
  });
  m.set("dot-blank", {
    variant: "dot-blank",
    fill: false,
    geometry: buildDotOutline(),
  });
  m.set("arch-tick", {
    variant: "arch-tick",
    fill: true,
    geometry: buildArchTick(),
  });
  m.set("oblique", {
    variant: "oblique",
    fill: true,
    geometry: buildObliqueTick(),
  });
  m.set("none", {
    variant: "none",
    fill: false,
    geometry: new THREE.BufferGeometry(), // empty — renderer skips
  });
  return m;
}

/**
 * Returns the singleton prototype for a variant. Lazily builds the
 * cache on first access. Disposing prototypes is the caller's
 * responsibility — module-level singletons survive scene unmount; the
 * 10 BufferGeometry instances together total <2 KB so this is cheap.
 */
export function getArrowPrototype(variant: ArrowVariant): ArrowPrototype {
  if (CACHE === null) CACHE = buildAllPrototypes();
  return CACHE.get(variant) ?? CACHE.get("closed-filled")!;
}

/** Returns all 10 prototypes (test helper / scene-side instancing setup). */
export function getAllArrowPrototypes(): readonly ArrowPrototype[] {
  if (CACHE === null) CACHE = buildAllPrototypes();
  return Array.from(CACHE.values());
}

/**
 * Dispose every cached prototype's BufferGeometry and reset the cache.
 * Call from hot-reload / explicit teardown — not called automatically.
 */
export function disposeArrowPrototypes(): void {
  if (CACHE === null) return;
  for (const p of CACHE.values()) p.geometry.dispose();
  CACHE = null;
}

/* ── DIMBLK / arrowhead block name → variant mapping ─────────────── */

/**
 * Map an AutoCAD arrowhead block name (DIMBLK / DIMBLK1 / DIMBLK2 or
 * MLENT_ARROWHEAD_BLOCK) to the renderer-side variant. Matches the
 * SVG renderer's `arrowMarkerIdFromBlockName` table.
 *
 * Empty / unknown / non-prefixed names default to `closed-filled`,
 * which is AutoCAD's default for an unset DIMBLK.
 */
export function dimblkToVariant(blockName: string): ArrowVariant {
  switch (blockName) {
    case "":
      return "closed-filled";
    case "_CLOSEDBLANK":
      return "closed-blank";
    case "_CLOSED":
      return "closed";
    case "_OPEN":
      return "open";
    case "_OPEN30":
      return "open30";
    case "_DOT":
      return "dot";
    case "_DOTBLANK":
      return "dot-blank";
    case "_ARCHTICK":
      return "arch-tick";
    case "_OBLIQUE":
      return "oblique";
    case "_NONE":
      return "none";
    default:
      return "closed-filled";
  }
}

/* ── Per-instance transform helpers (used by adapters / renderer) ── */

/**
 * Compute the rotation angle (radians, CCW from +X) needed to point an
 * arrow prototype at `direction`. The arrow's body lies along the
 * negative-X axis at unit scale — so for `direction = (1, 0)` (arrow
 * pointing right) the rotation angle is 0; for `direction = (0, 1)`
 * (pointing up) it is +π/2.
 */
export function arrowRotationFromDirection(direction: Vec3): number {
  return Math.atan2(direction.y, direction.x);
}

/**
 * The prototype-local UNIT axis along which a variant's nominal `size` is
 * measured — the one place the ten prototypes above state "which way does this
 * glyph's length run", shared by every consumer that must reduce a general 2×2
 * glyph map back to the single `size` scalar the map-less pipelines draw
 * (`adapters/typed/blockTransform.ts`'s block-child DIMENSION arm; the PDF
 * exporter's `print/pdf/converters/arrowGeometry.ts` similarity pose).
 *
 * # Why it is variant-dependent (Codex PR #784 round 11 P2, DIMENSION twin)
 *
 * Under the legacy `T(tip)·Rz(direction)·S(size, size)` pose EVERY prototype
 * axis rides the same scalar, so "which axis is the length" never mattered.
 * A block INSERT's linear part scales the along-direction and the
 * perpendicular by DIFFERENT factors, so the scalar that reproduces the glyph
 * under a similarity is `|M · u|` for THAT glyph's own `u` — and the three
 * families disagree about `u`:
 *
 *   - the ARROW families (`closed*`, `open*`, `dot*`) run their body one unit
 *     back along −X, i.e. along the arrow DIRECTION → `u = (1, 0)`;
 *   - `oblique` is a slash spanning local Y ∈ [−0.5, +0.5], PERPENDICULAR to
 *     the direction ({@link buildObliqueTick}) → `u = (0, 1)`;
 *   - `arch-tick` is a 45° slash spanning local (−0.45, −0.45) → (+0.45,
 *     +0.45) ({@link buildArchTick}) → `u = (1, 1)/√2`.
 *
 * `|Rz(θ)·S(s, s) · u| === s` for every unit `u`, so under a similarity all
 * three collapse to the pre-existing scalar — this only ever changes what a
 * NON-uniformly scaled block child reports.
 *
 * `none` has no geometry; it returns the arrow axis so callers need no
 * special case (nothing is drawn either way).
 */
export function glyphSizeAxis(variant: ArrowVariant): { x: number; y: number } {
  switch (variant) {
    case "oblique":
      return { x: 0, y: 1 };
    case "arch-tick":
      return { x: Math.SQRT1_2, y: Math.SQRT1_2 };
    default:
      return { x: 1, y: 0 };
  }
}
